/**
 * Page Performance console — the ONE module that talks to the server.
 *
 * Every read is `GET /api/v1/page-performance/…`, Master Admin only on the
 * server (`requireRole('super_admin')`), and cross-tenant by design: the
 * organisation filter is a filter, not a security boundary.
 *
 * Live behaviour (spec → Behaviour → Live):
 *   - every query refetches every 30 s, and only while the browser tab is
 *     visible (React Query pauses `refetchInterval` in the background);
 *   - at once when the tab becomes visible again (`refetchOnWindowFocus` —
 *     React Query's focus manager listens to `visibilitychange`);
 *   - the previous rows stay on screen while a refetch runs
 *     (`placeholderData`), so a live tick never blanks a table; a FILTER
 *     change is told apart by `isPlaceholderData` and shows a spinner.
 *
 * Responses are parsed leniently (api-schemas.js) so a small difference
 * between this build and the server's is a missing figure, never a crash.
 */
import { useCallback, useSyncExternalStore } from 'react'
import { keepPreviousData, useIsFetching, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../api'
import { getDisplayTimeZone } from '../format'
import { platformTenantsResponse } from '../types'
import {
  parseDetail,
  parseOrganisationsList,
  parseOverview,
  parsePagesList,
} from './api-schemas'

export const PERF_KEY = 'page-perf'
export const LIVE_MS = 30_000

const BASE = '/page-performance'

const liveOptions = {
  refetchInterval: LIVE_MS,
  refetchIntervalInBackground: false,
  refetchOnWindowFocus: true,
  placeholderData: keepPreviousData,
}

/** The viewer's display zone — "Today" starts at their midnight. */
export function viewerTimeZone() {
  const tz = getDisplayTimeZone()
  if (tz) return tz
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

/** The scope every read carries: organisation, period, compare, zone. */
export function scopeParams({ organization, period, compare, tz }) {
  return { organization: organization || 'all', period, compare, tz: tz || viewerTimeZone() }
}

/** Table params: search, status, sort, paging. Empty values are left out. */
function tableParams({ q, status, sort, dir, page, limit }) {
  const p = { page, limit }
  if (q && q.trim()) p.q = q.trim()
  if (status) p.status = status
  if (sort) {
    p.sort = sort
    p.dir = dir === 'asc' ? 'asc' : 'desc'
  }
  return p
}

async function getOverview(scope) {
  const res = await api.get(`${BASE}/overview`, { params: scopeParams(scope) })
  return parseOverview(res.data)
}

async function getPages(scope, table) {
  const res = await api.get(`${BASE}/pages`, {
    params: { ...scopeParams(scope), measured: 1, ...tableParams(table) },
  })
  return parsePagesList(res.data)
}

async function getOrganisations(scope, table) {
  const res = await api.get(`${BASE}/organizations`, {
    params: { ...scopeParams({ ...scope, organization: 'all' }), ...tableParams(table) },
  })
  return parseOrganisationsList(res.data)
}

async function getDetail(scope, route) {
  const res = await api.get(`${BASE}/pages/detail`, {
    params: { ...scopeParams(scope), route },
  })
  return parseDetail(res.data)
}

/**
 * The cache key carries the zone too: "Today" and the hour slots depend on
 * it, so a change of the viewer's zone (My Preferences) must refetch at
 * once rather than wait for the next live tick.
 */
function scopeKey({ organization, period, compare, tz }) {
  return { organization, period, compare, tz: tz || viewerTimeZone() }
}

export function useOverview(scope) {
  return useQuery({
    queryKey: [PERF_KEY, 'overview', scopeKey(scope)],
    queryFn: () => getOverview(scope),
    ...liveOptions,
  })
}

export function usePages(scope, table, { enabled = true } = {}) {
  return useQuery({
    queryKey: [PERF_KEY, 'pages', scopeKey(scope), table],
    queryFn: () => getPages(scope, table),
    enabled,
    ...liveOptions,
  })
}

export function useOrganisations(scope, table, { enabled = true } = {}) {
  return useQuery({
    queryKey: [PERF_KEY, 'organizations', scopeKey(scope), table],
    queryFn: () => getOrganisations(scope, table),
    enabled,
    ...liveOptions,
  })
}

export function useDetail(scope, route) {
  return useQuery({
    queryKey: [PERF_KEY, 'detail', scopeKey(scope), route],
    queryFn: () => getDetail(scope, route),
    enabled: Boolean(route),
    ...liveOptions,
  })
}

/** The largest page the server serves (contract: limit ≤ 100). */
export const MAX_LIMIT = 100

/** Every row of a list for "Export → all records" (bounded, 100 a request). */
async function fetchAll(getter, scope, table) {
  const all = []
  for (let page = 1; page <= 50; page += 1) {
    const list = await getter(scope, { ...table, page, limit: MAX_LIMIT })
    all.push(...list.rows)
    const total = list.total ?? all.length
    if (list.rows.length === 0 || all.length >= total) break
  }
  return all
}

export function fetchAllPages(scope, table) {
  return fetchAll(getPages, scope, table)
}

export function fetchAllOrganisations(scope, table) {
  return fetchAll(getOrganisations, scope, table)
}

/**
 * Every company, for the organisation filter. Same request and cache key as
 * the Visitor Logs / Audit Logs company filters, so the list is shared.
 */
export function useOrganisationOptions() {
  return useQuery({
    queryKey: ['platform-tenants-dropdown'],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const res = await api.get('/super-admin/tenants')
      return platformTenantsResponse.parse(res.data)
    },
  })
}

/**
 * The Live badge's state: when any Page Performance read last succeeded,
 * whether one is in flight, and a ⟳ that refetches every active one now.
 */
export function useLiveStatus() {
  const client = useQueryClient()
  const fetching = useIsFetching({ queryKey: [PERF_KEY] }) > 0
  const cache = client.getQueryCache()
  const subscribe = useCallback((onChange) => cache.subscribe(onChange), [cache])
  const getSnapshot = useCallback(() => {
    let latest = 0
    for (const q of cache.findAll({ queryKey: [PERF_KEY] })) {
      if (q.state.dataUpdatedAt > latest) latest = q.state.dataUpdatedAt
    }
    return latest
  }, [cache])
  const updatedAt = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)

  const refreshNow = useCallback(
    () => client.invalidateQueries({ queryKey: [PERF_KEY], refetchType: 'active' }),
    [client],
  )

  return { updatedAt, fetching, refreshNow }
}
